import { db } from './firebase';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';


// Example: termMap = { "1": "Fall", "2": "Spring", "3": "Summer" };

const handleAutofillCourse = async (
  allCourses,
  yearNumber,
  semester, // Now expected to be a number (e.g. 1, 2, 3, …)
  userId,
  templateName,
  numYearsNew,
  fetchedMajorElectives,
  fetchedMajorRequirements,
  fetchedMajorCourses,
  termMap
) => {
    const autofillUser = userId;
    const newScheduleName = templateName;
    const loadingToastId = 'autofill-course-toast';

  try {
    if (autofillUser) {
      const userRef = db.collection('users').doc(userId);
      const userDoc = await userRef.get();
      await db.runTransaction(async (transaction) => {
        let userData = userDoc.data();
        if (userDoc.exists) {
          userData = userDoc.data();
          console.log("User Data:", userData);
        } else {
          console.log("No such document!");
        }

        // Get the dynamic term name from the termMap based on the passed number
        const semesterKey = termMap[semester.toString()];
        if (!semesterKey) {
          throw new Error(`Term for semester number ${semester} not found in termMap.`);
        }

        const isComplete = userData?.completedSemesters?.[`year${yearNumber}`]?.[semesterKey];

        // If the semester is marked complete, copy from the first schedule
        if (isComplete) {
          if (Object.keys(userData?.schedules).length > 0) {
            const firstScheduleName = Object.keys(userData?.schedules)[0];
            const completedSemesterData = userData?.schedules?.[firstScheduleName]?.[`year${yearNumber}`]?.[semesterKey].courses;
            transaction.update(userRef, {
              [`schedules.${newScheduleName}.year${yearNumber}.${semesterKey}.courses`]: completedSemesterData,
            });
            return;
          } else {
            return;
          }
        }

        const previousFinalizedSchedule = userData.schedules[newScheduleName] || {};
        const updateObj = previousFinalizedSchedule ? { ...previousFinalizedSchedule } : {};

        if (!updateObj[`year${yearNumber}`]) {
          updateObj[`year${yearNumber}`] = {};
        }
        if (!updateObj[`year${yearNumber}`][semesterKey]) {
          updateObj[`year${yearNumber}`][semesterKey] = { courses: [], Placeholders: [] };
        }

        const existingFinalizedSchedule = updateObj[`year${yearNumber}`][semesterKey].courses;

        let allClassNumbers = [];
        for (const yr in updateObj) {
          for (const semKey in updateObj[yr]) {
            allClassNumbers = allClassNumbers.concat(updateObj[yr][semKey].courses);
          }
        }

        const allAutofillableCourses = allCourses.filter(
          (course) =>
            course.courseYear <= 4 &&
            course.Offering.includes(semesterKey) &&
            !allClassNumbers.includes(course.courseInfo_courseNumber) &&
            course.isPrereqMet
        );

        // Major Courses
        const combinedSemesterMajorCourses = [];
        Object.entries(fetchedMajorCourses).forEach(([majorName, courseInfo]) => {
          const organizedMajorCourses = courseInfo;
          if (organizedMajorCourses && organizedMajorCourses[`year${yearNumber}`]) {
            combinedSemesterMajorCourses.push(
              ...(organizedMajorCourses[`year${yearNumber}`][semesterKey] || [])
            );
          }
        });
        let semesterMajorCourses = combinedSemesterMajorCourses.length > 0
          ? combinedSemesterMajorCourses
          : [];
        const semesterMajorCoursesWithAutofill = semesterMajorCourses.map((course) => ({
          ...course,
          autofillType: 'Major Course',
        }));

        // Major Electives
        let combinedElectiveCourses = [];
        Object.entries(fetchedMajorElectives).forEach(([majorName, electivesByMajor]) => {
          if (electivesByMajor && electivesByMajor[`year${yearNumber}`]) {
            combinedElectiveCourses.push(
              ...electivesByMajor[`year${yearNumber}`][semesterKey]
            );
          }
        });
        const electiveNameStrings = combinedElectiveCourses.map(
          (course) => course.Possibilities
        );
        const semesterElectiveRequirements = electiveNameStrings.reduce((acc, name) => {
          if (!acc[name]) {
            const course = combinedElectiveCourses.find(
              (course) => course.Possibilities === name
            );
            const electiveFullName = course ? course.Requirements : '';
            acc[name] = { quantity: 0, electiveFullName: electiveFullName };
          }
          acc[name].quantity += 1;
          return acc;
        }, {});
        let autofillElectiveCourses = [];
        Object.entries(semesterElectiveRequirements).forEach(([requirementName, { quantity, electiveFullName }]) => {
          const existingCourses = existingFinalizedSchedule
            .map((courseNumber) =>
              allCourses.find((course) => course.courseInfo_courseNumber === courseNumber)
            )
            .filter((course) =>
              course && course.Elective_fulfillment.includes(requirementName)
            );
          const remainingQuantity = quantity - existingCourses.length;
          if (remainingQuantity > 0) {
            const filteredCourses = allAutofillableCourses.filter(
              (course) =>
                course.Elective_fulfillment.includes(requirementName) &&
                !allClassNumbers.includes(course.courseInfo_courseNumber)
            );
            const sortedCourses = filteredCourses.sort((a, b) => b.recScore - a.recScore);
            const topCourses = sortedCourses.slice(0, remainingQuantity).map((course) => ({
              ...course,
              electiveRequirement: requirementName,
              electiveFullName,
            }));
            autofillElectiveCourses.push(...topCourses);
          }
        });
        const autofillElectiveCoursesWithAutofill = autofillElectiveCourses.map((course) => ({
          ...course,
          autofillType: 'Elective',
        }));

        // Major Requirements
        let combinedRequirementCourses = [];
        Object.entries(fetchedMajorRequirements).forEach(([majorName, requirementsByMajor]) => {
          if (requirementsByMajor && requirementsByMajor[`year${yearNumber}`]) {
            combinedRequirementCourses.push(
              ...requirementsByMajor[`year${yearNumber}`][semesterKey]
            );
          }
        });
        const requirementNameStrings = combinedRequirementCourses.map(
          (course) => course.Requirements
        );
        const semesterRequirementDetails = requirementNameStrings.reduce((acc, name) => {
          if (!acc[name]) {
            const course = combinedRequirementCourses.find((course) => course.Requirements === name);
            const possibleCourses = course ? course.Possibilities.split(',').map((course) => course.trim()) : [];
            acc[name] = { quantity: 0, possibleCourses: possibleCourses };
          }
          acc[name].quantity += 1;
          return acc;
        }, {});
        let autofillRequirementCourses = [];
        Object.entries(semesterRequirementDetails).forEach(([requirementName, { quantity, possibleCourses }]) => {
          const commonElementsCount = possibleCourses.filter((course) =>
            existingFinalizedSchedule.includes(course)
          ).length;
          const remainingQuantity = quantity - commonElementsCount;
          if (remainingQuantity > 0) {
            const filteredCourses = possibleCourses
              .map((courseNumber) =>
                allAutofillableCourses.find(
                  (course) =>
                    course.courseInfo_courseNumber === courseNumber &&
                    !allClassNumbers.includes(courseNumber)
                )
              )
              .filter((course) => course !== undefined);
            const sortedCourses = filteredCourses.sort((a, b) => b.recScore - a.recScore);
            const topCourses = sortedCourses.slice(0, remainingQuantity).map((course) => ({
              ...course,
              requirementName: requirementName,
            }));
            autofillRequirementCourses.push(...topCourses);
          }
        });
        const autofillRequirementCoursesWithAutofill = autofillRequirementCourses.map((course) => ({
          ...course,
          autofillType: 'Requirement',
        }));

        // Combine all autofill courses, excluding minor courses
        const semesterAutofillCourses = [
          ...semesterMajorCoursesWithAutofill,
          ...autofillElectiveCoursesWithAutofill,
          ...autofillRequirementCoursesWithAutofill,
        ];

        const uniqueAutofillCourses = semesterAutofillCourses.reduce((acc, current) => {
          const x = acc.find(
            (item) => item.courseInfo_courseNumber === current.courseInfo_courseNumber
          );
          if (!x) {
            acc.push(current);
          }
          return acc.filter(
            (course) => !allClassNumbers.includes(course.courseInfo_courseNumber)
          );
        }, []);

        const semesterAutofillCoursesWithStatus = uniqueAutofillCourses.map((course) => ({
          ...course,
          isAutofill: true,
        }));

        const semesterAutofillCourseIds = semesterAutofillCoursesWithStatus.map(
          (course) => course.courseInfo_courseNumber
        );

        // Update the finalized schedule with the new autofilled courses
        const updatedFinalizedSchedule = [
          ...new Set([
            ...existingFinalizedSchedule,
            ...semesterAutofillCourseIds,
          ]),
        ];

        updateObj[`year${yearNumber}`][semesterKey].courses = updatedFinalizedSchedule;
        updateObj.numYears = numYearsNew;
        console.log('NumYears: ', numYearsNew, updateObj);

        transaction.update(userRef, {
          [`schedules.${newScheduleName}.year${yearNumber}.${semesterKey}.courses`]: updatedFinalizedSchedule,
          [`schedules.${newScheduleName}.numYears`]: numYearsNew
        });

        console.log('Courses Autofilled Successfully.');
        setTimeout(() => {
          toast.update(loadingToastId, {
            render: 'Applied Template',
            type: 'success',
            autoClose: 1000,
            isLoading: false,
            hideProgressBar: true,
            style: {
              width: '12rem',
            },
          });
        }, 500);
      });
    } else {
      console.error('Error: currentUser or currentUser.uid is not defined');
    }
  } catch (error) {
    setTimeout(() => {
      toast.update(loadingToastId, {
        render: 'Failed apply template.',
        type: 'error',
        autoClose: 3000,
        isLoading: false,
        style: {
          width: '20rem',
        },
      });
    }, 500);
    console.error('Error autofilling courses:', error);
  }
};

export default handleAutofillCourse;
